import { useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock, CheckCircle, ArrowRight, Upload, FileText, Image, Loader2, Save, Clock,
  LayoutDashboard, ClipboardList, FolderOpen, MessageSquare, ChevronRight
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import PortalPhase1 from "@/components/portal/PortalPhase1";
import PortalPhase2 from "@/components/portal/PortalPhase2";
import PortalPhase3 from "@/components/portal/PortalPhase3";
import PortalOffers from "@/components/portal/PortalOffers";
import { ThemeContext } from "@/components/portal/PortalShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASES = [
  { id: 1, label: "Pre-Kickoff", desc: "Bitte vor dem ersten Gespräch ausfüllen", time: "5–10 Min.", sections: "01–03" },
  { id: 2, label: "Kickoff Workshop", desc: "Gemeinsam im Workshop erarbeiten", time: "60 Min.", sections: "04–08" },
  { id: 3, label: "Technisch / IT", desc: "Intern oder mit IT-Kontakt besprechen", time: "30 Min.", sections: "09–12" },
];

const TIMELINE_STEPS = [
  { id: "kickoff_pending", label: "Kickoff", short: "KO" },
  { id: "in_progress", label: "In Bearbeitung", short: "IB" },
  { id: "review", label: "Review", short: "RE" },
  { id: "completed", label: "Abgeschlossen", short: "OK" },
];

const PORTAL_TABS = [
  { id: "overview", label: "Übersicht", icon: LayoutDashboard },
  { id: "questionnaire", label: "Fragebogen", icon: ClipboardList },
  { id: "documents", label: "Dokumente", icon: FolderOpen },
  { id: "messages", label: "Nachrichten", icon: MessageSquare },
];

const PHASE_THEMES = {
  1: { accent: "#D4A373", label: "#B5835A", bannerBg: "#D4A373", bannerText: "#0F172A", numBg: "#D4A373", numText: "#0F172A", focus: "#D4A373", badgeBg: "#D4A373", badgeText: "#0F172A", phaseNumOpacity: "0.18" },
  2: { accent: "#B5835A", label: "#B5835A", bannerBg: "#B5835A", bannerText: "#fff", numBg: "#B5835A", numText: "#fff", focus: "#B5835A", badgeBg: "#B5835A", badgeText: "#fff", phaseNumOpacity: "0.18" },
  3: { accent: "#64748B", label: "#64748B", bannerBg: "#1E293B", bannerText: "#F1F5F9", numBg: "#334155", numText: "#94A3B8", focus: "#64748B", badgeBg: "#334155", badgeText: "#94A3B8", phaseNumOpacity: "0.1" },
};

/* ───────────────────── Timeline Component ───────────────────── */
function ProjectTimeline({ currentStatus }) {
  const currentIdx = TIMELINE_STEPS.findIndex(s => s.id === currentStatus);
  const activeIdx = currentIdx === -1 ? 0 : currentIdx;

  return (
    <div className="flex items-center w-full" data-testid="project-timeline">
      {TIMELINE_STEPS.map((step, i) => {
        const isComplete = i < activeIdx;
        const isActive = i === activeIdx;
        return (
          <div key={step.id} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div
                className={`w-9 h-9 flex items-center justify-center text-xs font-bold transition-all ${
                  isComplete ? "bg-[#0F172A] text-white" :
                  isActive ? "bg-[#D4A373] text-[#0F172A] ring-4 ring-[#D4A373]/20" :
                  "bg-[#F1F5F9] text-[#94A3B8] border border-[#CBD5E1]"
                }`}
                data-testid={`timeline-step-${step.id}`}
              >
                {isComplete ? <CheckCircle className="w-4 h-4" /> : step.short}
              </div>
              <span className={`text-[0.65rem] mt-1.5 font-medium text-center leading-tight ${
                isActive ? "text-[#D4A373]" : isComplete ? "text-[#0F172A]" : "text-[#94A3B8]"
              }`}>
                {step.label}
              </span>
            </div>
            {i < TIMELINE_STEPS.length - 1 && (
              <div className={`h-[2px] flex-1 mx-2 mt-[-18px] ${
                i < activeIdx ? "bg-[#0F172A]" : "bg-[#CBD5E1]"
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ───────────────────── Main Portal ───────────────────── */
export default function PortalPage() {
  const { projectId } = useParams();
  const [authed, setAuthed] = useState(false);
  const [clientName, setClientName] = useState("");
  const [projectStatus, setProjectStatus] = useState("kickoff_pending");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [activePhase, setActivePhase] = useState(1);
  const [offers, setOffers] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const loadOffers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/portal/${projectId}/documents`);
      setOffers(res.data?.offers || []);
      setInvoices(res.data?.invoices || []);
    } catch (e) {
      console.error("Documents load error:", e);
      try {
        const res = await axios.get(`${API}/portal/${projectId}/offers`);
        setOffers(res.data || []);
      } catch (e2) { console.error("Offers fallback error:", e2); }
    }
  }, [projectId]);

  const loadExisting = useCallback(async () => {
    try {
      const [subRes, filesRes] = await Promise.all([
        axios.get(`${API}/portal/${projectId}/submission`),
        axios.get(`${API}/portal/${projectId}/files`)
      ]);
      if (subRes.data?.form_data) {
        setFormData(subRes.data.form_data);
        if (subRes.data.status === "submitted") setSubmitted(true);
      }
      setFiles(filesRes.data || []);
    } catch (e) { console.error("Portal load error:", e); }
    loadOffers();
  }, [projectId, loadOffers]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const res = await axios.post(`${API}/portal/${projectId}/auth`, { password });
      setClientName(res.data.client_name);
      setProjectStatus(res.data.project_status || "kickoff_pending");
      setAuthed(true);
      loadExisting();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentifizierung fehlgeschlagen");
    } finally {
      setAuthLoading(false);
    }
  };

  const updateField = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));
  const toggleCheckbox = (key, option) => {
    const current = formData[key] || [];
    updateField(key, current.includes(option) ? current.filter(o => o !== option) : [...current, option]);
  };

  const saveProgress = async (status = "in_progress") => {
    setSaving(true);
    try {
      await axios.post(`${API}/portal/${projectId}/submission`, { ...formData, status });
      if (status === "submitted") {
        setSubmitted(true);
        toast.success("Fragebogen erfolgreich eingereicht!");
      } else {
        toast.success("Fortschritt gespeichert");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const uploadFiles = Array.from(e.target.files);
    if (!uploadFiles.length) return;
    setUploading(true);
    try {
      for (const file of uploadFiles) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/portal/${projectId}/upload`, fd);
        if (res.data?.file) setFiles(prev => [res.data.file, ...prev]);
      }
      toast.success(`${uploadFiles.length} Datei(en) hochgeladen`);
    } catch (err) {
      console.error("File upload error:", err);
      toast.error("Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Computed values (memoized)
  const pendingOffers = useMemo(() => offers.filter(o => o.portal_status === "pending").length, [offers]);
  const totalOffers = offers.length;
  const openInvoices = useMemo(() => invoices.filter(i => i.status !== "paid").length, [invoices]);
  const allQuestions = useMemo(() => offers.flatMap(o => (o.questions || []).map(q => ({ ...q, offer_number: o.offer_number }))), [offers]);
  const filledFields = useMemo(() => Object.keys(formData).filter(k => formData[k] && formData[k] !== "" && (typeof formData[k] !== "object" || (Array.isArray(formData[k]) && formData[k].length > 0))).length, [formData]);

  /* ──── Password Gate ──── */
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-6" data-testid="portal-auth">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white p-10 max-w-md w-full border border-[#CBD5E1]">
          <div className="text-center mb-8">
            <div className="font-playfair text-2xl tracking-wide text-[#0F172A] mb-2">
              LAB<span className="text-[#D4A373]">336</span>
            </div>
            <p className="text-[#94A3B8] text-sm">Kundenportal</p>
          </div>
          <form onSubmit={handleAuth} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm text-[#1E293B] font-medium">Passwort</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  className="pl-10 rounded-none border-[#CBD5E1] focus:border-[#D4A373]"
                  placeholder="Projekt-Passwort eingeben" data-testid="portal-password-input" autoFocus />
              </div>
            </div>
            <button type="submit" disabled={authLoading || !password}
              className="w-full bg-[#0F172A] text-white py-3 flex items-center justify-center gap-2 text-sm font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-50"
              data-testid="portal-login-btn">
              {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Zugang erhalten
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  const theme = PHASE_THEMES[activePhase];
  const currentPhase = PHASES.find(p => p.id === activePhase);
  const formProps = { formData, updateField, toggleCheckbox, theme };

  /* ──── Main Portal Layout ──── */
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="portal-main">
      {/* ──── Header ──── */}
      <header className="bg-[#0F172A] border-b-[3px] border-[#D4A373]">
        <div className="max-w-[960px] mx-auto px-5 sm:px-8 lg:px-12 pt-8 pb-4">
          <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
            <div>
              <div className="font-playfair text-[1.6rem] font-semibold tracking-tight text-white"
                style={{ letterSpacing: "-0.02em" }}>
                LAB<span className="text-[#D4A373]">336</span>
              </div>
              <div className="text-[0.7rem] font-semibold uppercase text-[#64748B] mt-0.5"
                style={{ letterSpacing: "0.18em" }}>Kundenportal</div>
            </div>
            <div className="text-right">
              <div className="font-playfair text-lg font-semibold text-white">{clientName}</div>
              <div className="text-[0.7rem] text-[#64748B]">Projekt-ID: {projectId.slice(0, 8)}...</div>
            </div>
          </div>

          {/* Tab Navigation */}
          <nav className="flex gap-1" data-testid="portal-nav">
            {PORTAL_TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const badge = tab.id === "documents" && pendingOffers > 0 ? pendingOffers :
                            tab.id === "messages" && allQuestions.length > 0 ? allQuestions.length : 0;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all ${
                    isActive
                      ? "bg-[#D4A373] text-[#0F172A]"
                      : "text-[#94A3B8] hover:text-white hover:bg-[#1E293B]"
                  }`}
                  data-testid={`portal-tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  {badge > 0 && (
                    <span className={`text-[0.6rem] font-bold px-1.5 py-0.5 rounded-full ${
                      isActive ? "bg-[#0F172A] text-[#D4A373]" : "bg-[#D4A373] text-[#0F172A]"
                    }`}>{badge}</span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      {/* ──── Content ──── */}
      <main className="max-w-[960px] mx-auto px-5 sm:px-8 lg:px-12 py-8">
        <AnimatePresence mode="wait">

          {/* ════════ ÜBERSICHT ════════ */}
          {activeTab === "overview" && (
            <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="space-y-6" data-testid="portal-overview">

              {/* Welcome Card */}
              <div className="bg-[#0F172A] p-6 sm:p-8 border border-[#1E293B] relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.03]" style={{
                  backgroundImage: "url('https://images.pexels.com/photos/3610195/pexels-photo-3610195.jpeg?auto=compress&cs=tinysrgb&w=800')",
                  backgroundSize: "cover", backgroundPosition: "center"
                }} />
                <div className="relative">
                  <h1 className="font-playfair text-2xl sm:text-3xl font-semibold text-white mb-2">
                    Willkommen, {clientName}
                  </h1>
                  <p className="text-[#94A3B8] text-sm max-w-lg leading-relaxed">
                    {submitted
                      ? "Ihr Fragebogen wurde eingereicht. Hier finden Sie Ihre Angebote, Rechnungen und den aktuellen Projektstatus."
                      : "Füllen Sie den Fragebogen aus, damit wir optimal vorbereitet in den Kickoff starten können."
                    }
                  </p>
                  {!submitted && (
                    <button onClick={() => setActiveTab("questionnaire")}
                      className="mt-4 bg-[#D4A373] text-[#0F172A] px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-[#C4935F] transition-colors"
                      data-testid="portal-cta-questionnaire">
                      Fragebogen starten <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Status Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Fragebogen Status */}
                <button onClick={() => setActiveTab("questionnaire")}
                  className="bg-white border border-[#CBD5E1] p-5 text-left hover:border-[#D4A373] transition-colors group"
                  data-testid="portal-card-questionnaire">
                  <div className="flex items-center justify-between mb-3">
                    <ClipboardList className="w-5 h-5 text-[#D4A373]" />
                    <ChevronRight className="w-4 h-4 text-[#CBD5E1] group-hover:text-[#D4A373] transition-colors" />
                  </div>
                  <div className="text-xs font-semibold uppercase text-[#94A3B8] mb-1" style={{ letterSpacing: "0.12em" }}>Fragebogen</div>
                  <div className="font-playfair text-xl font-semibold text-[#0F172A]">
                    {submitted ? "Eingereicht" : `${filledFields} Felder`}
                  </div>
                  <div className={`text-xs mt-1 ${submitted ? "text-[#10B981]" : "text-[#94A3B8]"}`}>
                    {submitted ? "Erfolgreich übermittelt" : "In Bearbeitung"}
                  </div>
                </button>

                {/* Angebote Status */}
                <button onClick={() => setActiveTab("documents")}
                  className="bg-white border border-[#CBD5E1] p-5 text-left hover:border-[#D4A373] transition-colors group"
                  data-testid="portal-card-offers">
                  <div className="flex items-center justify-between mb-3">
                    <FileText className="w-5 h-5 text-[#D4A373]" />
                    <ChevronRight className="w-4 h-4 text-[#CBD5E1] group-hover:text-[#D4A373] transition-colors" />
                  </div>
                  <div className="text-xs font-semibold uppercase text-[#94A3B8] mb-1" style={{ letterSpacing: "0.12em" }}>Angebote</div>
                  <div className="font-playfair text-xl font-semibold text-[#0F172A]">{totalOffers}</div>
                  <div className={`text-xs mt-1 ${pendingOffers > 0 ? "text-[#D4A373]" : "text-[#94A3B8]"}`}>
                    {pendingOffers > 0 ? `${pendingOffers} offen` : "Alle bearbeitet"}
                  </div>
                </button>

                {/* Rechnungen Status */}
                <button onClick={() => setActiveTab("documents")}
                  className="bg-white border border-[#CBD5E1] p-5 text-left hover:border-[#D4A373] transition-colors group"
                  data-testid="portal-card-invoices">
                  <div className="flex items-center justify-between mb-3">
                    <FolderOpen className="w-5 h-5 text-[#D4A373]" />
                    <ChevronRight className="w-4 h-4 text-[#CBD5E1] group-hover:text-[#D4A373] transition-colors" />
                  </div>
                  <div className="text-xs font-semibold uppercase text-[#94A3B8] mb-1" style={{ letterSpacing: "0.12em" }}>Rechnungen</div>
                  <div className="font-playfair text-xl font-semibold text-[#0F172A]">{invoices.length}</div>
                  <div className={`text-xs mt-1 ${openInvoices > 0 ? "text-[#F59E0B]" : "text-[#94A3B8]"}`}>
                    {openInvoices > 0 ? `${openInvoices} offen` : invoices.length > 0 ? "Alle bezahlt" : "Keine vorhanden"}
                  </div>
                </button>
              </div>

              {/* Timeline */}
              <div className="bg-white border border-[#CBD5E1] p-6">
                <h3 className="text-xs font-semibold uppercase text-[#94A3B8] mb-5" style={{ letterSpacing: "0.15em" }}>Projektstatus</h3>
                <ProjectTimeline currentStatus={projectStatus} />
              </div>
            </motion.div>
          )}

          {/* ════════ FRAGEBOGEN ════════ */}
          {activeTab === "questionnaire" && (
            <motion.div key="questionnaire" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              data-testid="portal-questionnaire">

              {submitted ? (
                <div className="bg-white p-8 border border-[#CBD5E1] text-center">
                  <CheckCircle className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
                  <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-2">Fragebogen eingereicht</h2>
                  <p className="text-[#94A3B8] text-sm">Vielen Dank! Wir melden uns in Kürze bei Ihnen.</p>
                </div>
              ) : (
                <>
                  <style>{`
                    .portal-input {
                      width: 100%; border: 1.5px solid #CBD5E1; background: #F8F9FA; color: #1E293B;
                      font-family: 'IBM Plex Sans', sans-serif; font-size: 0.9375rem; padding: 0.625rem 0.875rem;
                      border-radius: 0; outline: none; transition: border-color 0.15s;
                    }
                    .portal-input:focus { border-color: ${theme.focus}; background: #fff; }
                    .portal-textarea { resize: vertical; min-height: 72px; }
                    .portal-textarea.tall { min-height: 110px; }
                  `}</style>

                  {/* Phase Tabs */}
                  <div className="flex gap-2 mb-6 flex-wrap">
                    {PHASES.map(phase => {
                      const t = PHASE_THEMES[phase.id];
                      const isActive = activePhase === phase.id;
                      return (
                        <button key={phase.id} onClick={() => setActivePhase(phase.id)}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all border"
                          style={{
                            background: isActive ? t.badgeBg : "#fff",
                            color: isActive ? t.badgeText : "#64748B",
                            borderColor: isActive ? t.badgeBg : "#CBD5E1",
                          }}
                          data-testid={`phase-tab-${phase.id}`}>
                          <span className="text-[0.625rem] font-bold px-1.5 py-0.5"
                            style={{
                              background: isActive ? "#0F172A" : "#F1F5F9",
                              color: isActive ? t.accent : "#64748B",
                            }}>{phase.id}</span>
                          {phase.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Phase Banner */}
                  <div className="p-5 mb-6" style={{ background: theme.bannerBg, borderBottom: activePhase === 3 ? "2px solid #334155" : "none" }}>
                    <div className="grid gap-4 items-center" style={{ gridTemplateColumns: "auto 1fr" }}>
                      <div className="font-playfair text-4xl font-bold leading-none" style={{ color: theme.bannerText, opacity: theme.phaseNumOpacity }}>{activePhase}</div>
                      <div>
                        <h2 className="font-playfair text-base font-semibold mb-0.5" style={{ color: theme.bannerText }}>
                          {currentPhase.label} — {currentPhase.desc}
                        </h2>
                        <span className="inline-flex items-center gap-1.5 text-[0.7rem] font-bold uppercase px-2 py-0.5 mt-1"
                          style={{ color: theme.bannerText, border: `1.5px solid ${theme.bannerText}`, opacity: 0.6, letterSpacing: "0.15em" }}>
                          <Clock className="w-3 h-3" /> ca. {currentPhase.time}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Phase Content */}
                  <ThemeContext.Provider value={theme}>
                    {activePhase === 1 && <PortalPhase1 {...formProps} />}
                    {activePhase === 2 && <PortalPhase2 {...formProps} />}
                    {activePhase === 3 && <PortalPhase3 {...formProps} />}
                  </ThemeContext.Provider>

                  {/* File Upload */}
                  <div className="bg-white border border-[#CBD5E1] mb-6 mt-6" data-testid="section-files">
                    <div className="bg-[#0F172A] flex items-center gap-3.5 px-6 py-3.5">
                      <span className="text-[0.7rem] font-bold px-2 py-0.5"
                        style={{ background: theme.numBg, color: theme.numText, letterSpacing: "0.1em" }}>+</span>
                      <span className="font-playfair text-base font-semibold text-white">Dateien & Anhänge</span>
                    </div>
                    <div className="px-7 py-6">
                      <label className="block border-2 border-dashed border-[#CBD5E1] p-8 text-center cursor-pointer hover:border-[#D4A373] transition-colors"
                        data-testid="file-upload-area">
                        <input type="file" multiple onChange={handleFileUpload} className="hidden" />
                        {uploading ? (
                          <Loader2 className="w-7 h-7 text-[#D4A373] mx-auto animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-7 h-7 text-[#94A3B8] mx-auto mb-2" />
                            <p className="text-sm text-[#94A3B8]">Dateien hierher ziehen oder klicken</p>
                            <p className="text-xs text-[#94A3B8] mt-0.5">PDF, JPG, PNG, DOCX, XLSX etc.</p>
                          </>
                        )}
                      </label>
                      {files.length > 0 && (
                        <div className="space-y-1.5 mt-4">
                          {files.map(f => (
                            <div key={f.id} className="flex items-center gap-3 bg-[#F8F9FA] p-2.5 border border-[#CBD5E1]">
                              {f.content_type?.startsWith("image/") ? (
                                <Image className="w-4 h-4 text-[#B5835A] shrink-0" />
                              ) : (
                                <FileText className="w-4 h-4 text-[#B5835A] shrink-0" />
                              )}
                              <span className="text-sm text-[#1E293B] truncate flex-1">{f.filename}</span>
                              <span className="text-xs text-[#94A3B8]">{(f.size / 1024).toFixed(0)} KB</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-4 border-t border-[#CBD5E1]">
                    <button onClick={() => saveProgress("in_progress")} disabled={saving}
                      className="flex items-center gap-2 text-sm border border-[#CBD5E1] px-5 py-2.5 text-[#1E293B] hover:bg-white transition-colors"
                      data-testid="portal-save-btn">
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Zwischenspeichern
                    </button>
                    <div className="flex items-center gap-3">
                      {activePhase < 3 && (
                        <button onClick={() => { saveProgress("in_progress"); setActivePhase(activePhase + 1); window.scrollTo(0, 0); }}
                          className="flex items-center gap-2 text-sm border border-[#0F172A] px-5 py-2.5 text-[#0F172A] hover:bg-[#F8F9FA] transition-colors"
                          data-testid="portal-next-phase-btn">
                          Weiter zu Phase {activePhase + 1} <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {activePhase === 3 && (
                        <button onClick={() => saveProgress("submitted")} disabled={saving}
                          className="bg-[#0F172A] text-white px-6 py-2.5 flex items-center gap-2 text-sm font-medium hover:bg-[#1E293B] transition-colors"
                          data-testid="portal-submit-btn">
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                          Einreichen
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          )}

          {/* ════════ DOKUMENTE ════════ */}
          {activeTab === "documents" && (
            <motion.div key="documents" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              data-testid="portal-documents">
              <PortalOffers projectId={projectId} offers={offers} invoices={invoices} onUpdate={loadOffers} />
              {offers.length === 0 && invoices.length === 0 && (
                <div className="bg-white border border-[#CBD5E1] p-12 text-center">
                  <FolderOpen className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" />
                  <p className="text-[#94A3B8] text-sm">Noch keine Dokumente vorhanden.</p>
                  <p className="text-[#CBD5E1] text-xs mt-1">Sobald Angebote oder Rechnungen erstellt werden, erscheinen sie hier.</p>
                </div>
              )}
            </motion.div>
          )}

          {/* ════════ NACHRICHTEN ════════ */}
          {activeTab === "messages" && (
            <motion.div key="messages" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="space-y-4" data-testid="portal-messages">
              <div className="bg-white border border-[#CBD5E1]">
                <div className="bg-[#0F172A] px-6 py-3.5">
                  <span className="font-playfair text-base font-semibold text-white">Nachrichten & Rückfragen</span>
                </div>
                {allQuestions.length === 0 ? (
                  <div className="p-12 text-center">
                    <MessageSquare className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" />
                    <p className="text-[#94A3B8] text-sm">Noch keine Nachrichten.</p>
                    <p className="text-[#CBD5E1] text-xs mt-1">
                      Sie können bei jedem Angebot unter "Dokumente" eine Rückfrage stellen.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#F1F5F9]">
                    {allQuestions.sort((a, b) => (b.asked_at || b.replied_at || "").localeCompare(a.asked_at || a.replied_at || "")).map((q, i) => (
                      <div key={i} className="px-6 py-4">
                        {/* Customer question */}
                        {q.question && (
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-[#D4A373]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <MessageSquare className="w-4 h-4 text-[#D4A373]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-[#0F172A]">{q.question}</p>
                              <div className="flex items-center gap-3 mt-1.5">
                                <span className="text-[0.65rem] text-[#94A3B8]">Angebot {q.offer_number}</span>
                                <span className="text-[0.65rem] text-[#CBD5E1]">
                                  {q.asked_at ? new Date(q.asked_at).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Admin reply */}
                        {q.reply && (
                          <div className="flex items-start gap-3 mt-3 ml-11">
                            <div className="w-8 h-8 bg-[#0F172A] flex items-center justify-center flex-shrink-0 mt-0.5">
                              <ArrowRight className="w-4 h-4 text-[#D4A373]" />
                            </div>
                            <div className="flex-1 min-w-0 bg-[#F8F9FA] p-3 border-l-3 border-[#D4A373]" style={{ borderLeft: "3px solid #D4A373" }}>
                              <p className="text-sm text-[#0F172A]">{q.reply.reply || q.reply}</p>
                              <span className="text-[0.65rem] text-[#94A3B8] mt-1 block">
                                LAB336 — {q.reply.replied_at ? new Date(q.reply.replied_at).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                              </span>
                            </div>
                          </div>
                        )}
                        {/* Admin message (not reply) */}
                        {q.type === "message" && !q.question && (
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-[#0F172A] flex items-center justify-center flex-shrink-0 mt-0.5">
                              <ArrowRight className="w-4 h-4 text-[#D4A373]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-[#0F172A]">{q.reply?.reply || q.reply}</p>
                              <span className="text-[0.65rem] text-[#94A3B8] mt-1 block">LAB336</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </main>

      {/* ──── Footer ──── */}
      <footer className="bg-[#0F172A] text-[#475569] px-5 sm:px-8 lg:px-12 py-5 mt-auto">
        <div className="max-w-[960px] mx-auto flex justify-between items-center flex-wrap gap-4 text-[0.75rem] font-medium uppercase" style={{ letterSpacing: "0.12em" }}>
          <span className="font-playfair font-semibold text-[0.875rem] text-[#D4A373] normal-case" style={{ letterSpacing: "-0.01em" }}>LAB336</span>
          <span>Vertraulich — nur für den internen Gebrauch</span>
        </div>
      </footer>
    </div>
  );
}
